/**
 * 名字 → 角色 - 主入口
 * 状态机驱动的单页应用
 */

import "./style.css";
import { createScreen0, type Screen0Controller } from "./screens/screen0";
import { createScreen1, type Screen1Controller } from "./screens/screen1";
import {
  createScreen2,
  type Screen2Controller,
} from "./screens/screen2";
import { pickRandomCombos, type Screen2Combo } from "./combo";
import { createScreen3, type Screen3Controller } from "./screens/screen3";
import { createScreen4, type Screen4Controller } from "./screens/screen4";
import { addToZoo, cleanupZoo, refreshZoo } from "./zoo";
import { createScreen5, type Screen5Controller } from "./screens/screen5";
import { saveLastGeneration } from "./lastGen";
import { getSfx } from "./bgm";
import type { CharAnalysis, MonsterDesc } from "../shared/types";

const FONT_FAMILY = "Vonwaon, monospace";

interface GridState {
  lastName: string;
  analysis: CharAnalysis;
  monsters: MonsterDesc[];
  combos: Screen2Combo[];
}

type Screen =
  | { name: "0"; ctrl: Screen0Controller }
  | { name: "1"; ctrl: Screen1Controller; onRetry: () => void; onCancel: () => void }
  | { name: "2"; ctrl: Screen2Controller } & GridState
  | { name: "3"; ctrl: Screen3Controller } & GridState & { selectedIndex: number }
  | { name: "4"; ctrl: Screen4Controller }
  | { name: "5"; ctrl: Screen5Controller };

let current: Screen | null = null;
let appRoot: HTMLElement;

function switchTo(next: Screen): void {
  if (current) {
    current.ctrl.destroy();
  }
  current = next;
}

function startApp(): void {
  // 启动时清理 zoo 旧数据（去重同名条目）
  cleanupZoo();
  // 启动时拉一次远端共享名称库（屏幕 0 状态栏 + 屏幕 4 金字塔 都需要）
  void refreshZoo();

  appRoot = document.getElementById("app")!;

  // 全局按钮 / 链接点击 → 播放清脆"叮"声
  // 匹配：button / .nav-btn / .duel-action / .result-action / .submit-btn /
  //       a[href] / .link / .link-btn / [data-click-sfx] / [role="button"]
  document.addEventListener("click", (e) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    if (target.closest(
      "button, .nav-btn, .duel-action, .result-action, .submit-btn, " +
      ".link, .link-btn, a[href], [data-click-sfx], [role=\"button\"]"
    )) {
      try { getSfx().playClick(); } catch { /* ignore */ }
    }
  });

  const goToScreen4 = () => {
    switchTo({ name: "4", ctrl: createScreen4({
      root: appRoot,
      onBack: () => goToScreen0(),
    }) });
  };

  const goToScreen5 = () => {
    switchTo({ name: "5", ctrl: createScreen5({
      root: appRoot,
      onBack: () => goToScreen0(),
    }) });
  };

  const goToScreen0 = () => {
    switchTo({ name: "0", ctrl: createScreen0({
      root: appRoot,
      fontFamily: FONT_FAMILY,
      onMonsters: (name, analysis, monsters) => {
        // API 现在只返回 1 只怪；取第一个作为预览和详情目标
        const monster: MonsterDesc = { ...monsters[0], element: analysis.dominantElement };
        const dominant = analysis.dominantElement ?? "土";
        const combos = pickRandomCombos(1, dominant);
        const combo = combos[0];
        saveLastGeneration([monster], [combo]);
        // 立即入园（无论用户后续去预览/详情/图鉴，怪都在圈养间里）
        // 同名不入园（同一名字五行 + 分数固定不变）
        const zooResult = addToZoo(monster, combo, name, analysis.fortune, analysis.fame);
        switchTo({
          name: "1",
          ctrl: createScreen1({
            root: appRoot,
            onRetry: () => goToScreen0(),
            onCancel: () => goToScreen0(),
          }),
          onRetry: () => goToScreen0(),
          onCancel: () => goToScreen0(),
        });
        setTimeout(() => {
          if (current?.name !== "1") return;
          goToScreen2(name, analysis, monster, combo, zooResult);
        }, 1200);
      },
      onError: (msg) => {
        switchTo({
          name: "1",
          ctrl: createScreen1({
            root: appRoot,
            onRetry: () => goToScreen0(),
            onCancel: () => goToScreen0(),
          }),
          onRetry: () => goToScreen0(),
          onCancel: () => goToScreen0(),
        });
        if (current?.name === "1") {
          current.ctrl.showError(msg);
        }
      },
      onGoZoo: () => goToScreen4(),
      onGoBattle: () => goToScreen5(),
    }) });
  };

  const goToScreen2 = (name: string, analysis: CharAnalysis, monster: MonsterDesc, combo: Screen2Combo, zooResult: { added: boolean; reason?: "duplicate" }) => {
    switchTo({
      name: "2",
      ctrl: createScreen2({
        root: appRoot,
        fontFamily: FONT_FAMILY,
        analysis,
        zooResult,
        onRegenerate: () => goToScreen0(),
        onBack: () => goToScreen0(),
        onGoZoo: () => goToScreen4(),
      }),
      lastName: name,
      analysis,
      monsters: [monster],
      combos: [combo],
    });
  };

  const goToScreen3 = (
    name: string,
    analysis: CharAnalysis,
    monster: MonsterDesc,
    combo: Screen2Combo
  ) => {
    switchTo({
      name: "3",
      ctrl: createScreen3({
        root: appRoot,
        analysis,
        monster,
        combo,
        onBack: () => goToScreen2(name, analysis, monster, combo, { added: true }),
        onRegenerate: () => goToScreen0(),
        onGoZoo: () => goToScreen4(),
      }),
      lastName: name,
      analysis,
      monsters: [monster],
      combos: [combo],
      selectedIndex: 0,
    });
  };

  goToScreen0();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", startApp);
} else {
  startApp();
}
